import { Form, Formik, type FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { AddAgencySchema } from '../agency.schema';
import AgencyForm from './AgencyForm';
import { type AgencyRequest } from '../../../common/types';
import { extractErrorsFromApi } from '../../../common/helpers';
import RenderErrorBag from '../../../components/render-error-bag';
import { toast } from 'react-toastify';
import { useAddAgencyMutation } from '../agency.api-slice';
import { useTranslation } from 'react-i18next';

interface IProps {
  isOpen: boolean;
  toggleModal: () => any;
}

const initialData: AgencyRequest = {
  name: '',
  email: '',
  phone: '',
  address: ''
};

const AddAgency: React.FC<IProps> = ({ isOpen, toggleModal }) => {
  const [addItem, { isLoading, isError, error, isSuccess, reset }] = useAddAgencyMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const { t } = useTranslation();

  const handleAddItem = async (values: AgencyRequest, actions: FormikHelpers<AgencyRequest>): Promise<any> => {
    await addItem({ ...values });
    actions.resetForm();
    toggleModal();
  };

  useEffect(() => {
    if (isSuccess) {
      toast(t('agencies.add.successMessage'));
      reset();
    }
    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [isError, isSuccess]);

  return (
    <>
      {isOpen && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 data-cy="add-agency-modal-title" className="text-3xl font-semibold">
                    {t('agencies.add.title')}
                  </h3>
                </div>
                <div className="relative p-6 flex-auto w-full">
                  {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
                  {
                    <Formik initialValues={initialData} onSubmit={handleAddItem} validationSchema={AddAgencySchema}>
                      {({ isSubmitting }) => (
                        <Form className="w-full flex flex-col items-start px-2" autoComplete="off">
                          <AgencyForm />
                          <div className="w-full flex space-x-2 py-6 mt-4 border-t border-solid border-slate-200 rounded-b">
                            <button className="w-1/2 text-black px-5 py-2 rounded-lg bg-gray-200" onClick={toggleModal}>
                              {t('agencies.add.cancelBtnLabel')}
                            </button>
                            <button
                              type="submit"
                              data-cy="agency-submit-btn"
                              disabled={isLoading || isSubmitting}
                              className="w-1/2 text-white px-4 py-2 rounded-lg bg-[#1D8A43]"
                            >
                              {t('agencies.add.saveBtnLabel')}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </>
  );
};

export default AddAgency;
